import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';

/**
 * URL query string 為唯一真實來源（single source of truth）的查詢狀態 hook。
 *
 * - render 時直接由 `searchParams` parse 出 `values`，不另存 useState 鏡像，
 *   避免雙來源不一致與無限迴圈。
 * - 只有事件處理（onChange / debounce）才透過 `setValues` 寫回 URL。
 * - 與既有 `usePagination({ syncUrl: true })` 並存：各自只讀寫自己的 query key，
 *   共用同一份 `useSearchParams`，互不覆蓋。
 */

type SetEntries = Array<[string, string]>;

export interface QueryParamCodec<T> {
  /** 此 codec 擁有的 URL key（更新時會先清掉這些 key 再重寫） */
  keys: string[];
  parse: (sp: URLSearchParams) => T;
  /** 回傳要寫入的 entries；空陣列代表清除（不寫任何值） */
  serialize: (value: T) => SetEntries;
}

export const QP = {
  string(key: string, def = ''): QueryParamCodec<string> {
    return {
      keys: [key],
      parse: (sp) => sp.get(key) ?? def,
      serialize: (v) => (v ? [[key, v]] : []),
    };
  },

  stringArray(key: string): QueryParamCodec<string[] | undefined> {
    return {
      keys: [key],
      parse: (sp) => {
        const all = sp.getAll(key);
        return all.length ? all : undefined;
      },
      serialize: (v) =>
        v && v.length ? v.map((x): [string, string] => [key, x]) : [],
    };
  },

  boolean(key: string): QueryParamCodec<boolean | undefined> {
    return {
      keys: [key],
      parse: (sp) => {
        const raw = sp.get(key);
        if (raw === 'true') return true;
        if (raw === 'false') return false;
        return undefined;
      },
      serialize: (v) =>
        typeof v === 'boolean' ? [[key, v ? 'true' : 'false']] : [],
    };
  },

  dayjsRange(
    startKey: string,
    endKey: string
  ): QueryParamCodec<{ startTime: Dayjs | undefined; endTime: Dayjs | undefined }> {
    return {
      keys: [startKey, endKey],
      parse: (sp) => {
        const s = sp.get(startKey);
        const e = sp.get(endKey);
        return {
          startTime: s ? dayjs(s) : undefined,
          endTime: e ? dayjs(e) : undefined,
        };
      },
      serialize: ({ startTime, endTime }) => {
        const entries: SetEntries = [];
        if (startTime) entries.push([startKey, startTime.toISOString()]);
        if (endTime) entries.push([endKey, endTime.toISOString()]);
        return entries;
      },
    };
  },
};

type Schema = Record<string, QueryParamCodec<any>>;
type Values<S extends Schema> = {
  [K in keyof S]: ReturnType<S[K]['parse']>;
};

export function useQueryParams<S extends Schema>(schema: S) {
  const [searchParams, setSearchParams] = useSearchParams();

  const values = useMemo(() => {
    const out: Record<string, unknown> = {};
    for (const field in schema) {
      out[field] = schema[field].parse(searchParams);
    }
    return out as Values<S>;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, schema]);

  const setValues = useCallback(
    (
      patch: Partial<Values<S>>,
      {
        resetPage = true,
        replace = true,
      }: { resetPage?: boolean; replace?: boolean } = {}
    ) => {
      const next = new URLSearchParams(searchParams.toString());

      (Object.keys(patch) as Array<keyof S>).forEach((field) => {
        const codec = schema[field];
        if (!codec) return;
        codec.keys.forEach((k) => next.delete(k));
        codec
          .serialize(patch[field] as ReturnType<S[typeof field]['parse']>)
          .forEach(([k, v]) => next.append(k, v));
      });

      if (resetPage) {
        next.set('page', '1');
      }

      setSearchParams(next, { replace });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchParams, setSearchParams, schema]
  );

  return { values, setValues };
}
